import { useMemo, useState } from 'react';
import { CrmBoard, CrmNegocio } from '@/types/crm';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowUp, ArrowUpDown, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/dateOnly';
import { format } from 'date-fns';
import { diasSemAtualizar, DIAS_PARADO_AMBAR, DIAS_PARADO_VERMELHO } from '@/lib/crmNegocioStatus';
import { tagColor } from '@/lib/tagColor';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getIniciais } from '@/lib/avatarIniciais';
import { formatMoeda } from '@/lib/formatMoeda';

interface CrmNegocioListViewProps {
  board: CrmBoard;
  negocios: CrmNegocio[];
  onRowClick: (negocio: CrmNegocio) => void;
}

type SortField = 'titulo' | 'valor' | 'data_previsao_fechamento' | 'createdAt';
type SortDirection = 'asc' | 'desc';

export function CrmNegocioListView({ board, negocios, onRowClick }: CrmNegocioListViewProps) {
  const [sortField, setSortField] = useState<SortField>('titulo');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const colunaPorId = useMemo(() => {
    const map = new Map<string, (typeof board.colunas)[number]>();
    (board.colunas || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [board.colunas]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const negociosOrdenados = useMemo(() => {
    const sign = sortDirection === 'asc' ? 1 : -1;
    return [...negocios].sort((a, b) => {
      if (sortField === 'valor') {
        return ((Number(a.valor) || 0) - (Number(b.valor) || 0)) * sign;
      }
      if (sortField === 'data_previsao_fechamento') {
        const da = a.data_previsao_fechamento || '';
        const db = b.data_previsao_fechamento || '';
        return da.localeCompare(db) * sign;
      }
      if (sortField === 'createdAt') {
        const da = a.createdAt || '';
        const db = b.createdAt || '';
        return da.localeCompare(db) * sign;
      }
      return a.titulo.localeCompare(b.titulo) * sign;
    });
  }, [negocios, sortField, sortDirection]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground" />;
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const SortableHead = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead>
      <button
        type="button"
        onClick={() => handleSort(field)}
        className="flex items-center hover:text-foreground"
      >
        {children}
        <SortIcon field={field} />
      </button>
    </TableHead>
  );

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead field="titulo">Negócio</SortableHead>
            <TableHead>Estágio</TableHead>
            <TableHead>Conta</TableHead>
            <TableHead>Responsável</TableHead>
            <SortableHead field="valor">Valor</SortableHead>
            <SortableHead field="data_previsao_fechamento">Previsão</SortableHead>
            <SortableHead field="createdAt">Criado em</SortableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {negociosOrdenados.map((negocio) => {
            const coluna = colunaPorId.get(negocio.column_id);
            const dias = diasSemAtualizar(negocio.updatedAt);
            const parado = dias !== null && dias >= DIAS_PARADO_AMBAR;
            return (
              <TableRow
                key={negocio.id}
                className="cursor-pointer"
                onClick={() => onRowClick(negocio)}
              >
                <TableCell>
                  <div className="font-medium">{negocio.titulo}</div>
                  {negocio.tags && negocio.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {negocio.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="px-1.5 py-0 text-[10px] font-normal"
                          style={{ borderColor: tagColor(tag), color: tagColor(tag) }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {parado && (
                    <span
                      className={cn(
                        'mt-0.5 flex items-center gap-1 text-xs font-medium',
                        dias! >= DIAS_PARADO_VERMELHO ? 'text-destructive' : 'text-amber-600 dark:text-amber-500',
                      )}
                    >
                      <Clock className="h-3 w-3" /> sem atualização há {dias}d
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {coluna && (
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: coluna.cor }} />
                      {coluna.nome}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {negocio.conta?.razao_social || '—'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {negocio.responsaveis && negocio.responsaveis.length > 0 && (
                    <span className="flex items-center -space-x-1.5">
                      {negocio.responsaveis.map((responsavel) => (
                        <Tooltip key={responsavel.id}>
                          <TooltipTrigger asChild>
                            <Avatar className="h-5 w-5 border border-background">
                              <AvatarImage src={responsavel.avatar_url ?? undefined} alt={responsavel.nome} />
                              <AvatarFallback className="text-[10px]">{getIniciais(responsavel.nome)}</AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent>{responsavel.nome}</TooltipContent>
                        </Tooltip>
                      ))}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm font-medium">{formatMoeda(negocio.valor) ?? '—'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {negocio.data_previsao_fechamento
                    ? format(parseDateOnly(negocio.data_previsao_fechamento), 'dd/MM/yyyy')
                    : '—'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {negocio.createdAt ? format(new Date(negocio.createdAt), 'dd/MM/yyyy') : '—'}
                </TableCell>
              </TableRow>
            );
          })}
          {negociosOrdenados.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                Nenhum negócio encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
